import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
	NgbModal,
	NgbModalRef,
	NgbAlertModule,
	NgbDropdownModule,
	NgbNavModule,
} from '@ng-bootstrap/ng-bootstrap';
import { trigger, transition, style, animate } from '@angular/animations';

import { LoginService } from '../../login-service/login.service';
import { NotificationService } from '../../notification-service/notification.service';
import {
	OrgReturn,
	OrgRequest,
	ErrorResponse,
	UIAnimations,
} from '@meta-press/shared-plugins';
import { BackendService } from '../../backend-service/backend.service';
import { ConfirmModalComponent } from '../../components/confirm-modal/confirm-modal.component';

@Component({
	selector: 'app-organization-management',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		NgbAlertModule,
		NgbDropdownModule,
		NgbNavModule,
	],
	templateUrl: './organization-management.component.html',
	styleUrl: './organization-management.component.scss',
	animations: UIAnimations,
})
export class OrganizationManagementComponent implements OnInit {
	@ViewChild('addOrgModal') addOrgModalTemplate!: TemplateRef<unknown>;
	@ViewChild('editOrgModal') editOrgModalTemplate!: TemplateRef<unknown>;

	protected organizations: OrgReturn[] = [];
	protected filteredOrganizations: OrgReturn[] = [];
	protected searchText = '';
	protected hasError = false;
	protected errorMessage = '';
	get isLoading(): boolean {
		return this.loginService.IsLoadingOrganizations;
	}
	
	// Sorting properties
	sortColumn: 'name' | 'userCount' | null = null;
	sortDirection: 'asc' | 'desc' = 'asc';

	errors: string[] = [];

	// New organization form data
	protected newOrganization: {
		name: string;
	} = {
		name: '',
	};

	// Organization being edited
	protected editingOrganization: OrgReturn | null = null;

	private addOrgModalRef: NgbModalRef | undefined;
	private editOrgModalRef: NgbModalRef | undefined;

	protected isCreatingOrganization = false;
	protected isUpdatingOrganization = false;

	constructor(
		protected loginService: LoginService,
		private modalService: NgbModal,
		private notificationService: NotificationService,
		private backendService: BackendService,
	) {
		// Subscribe to organization list updates
		this.loginService.OnOrganizationList.subscribe((orgs: OrgReturn[]) => {
			if (orgs && orgs.length > 0) {
				this.organizations = [...orgs];
				this.filterOrganizations();
			} else {
				this.organizations = [];
				this.filteredOrganizations = [];
			}
			// Data received - loading state is handled by login service
		});
	}

	ngOnInit(): void {
		// Lazy load organizations when component initializes
		this.loginService.ensureOrganizationsLoaded().subscribe({
			next: (loaded) => {
				if (!loaded) {
					console.error('Failed to load organizations');
					this.hasError = true;
					this.errorMessage = 'Failed to load organizations';
				}
			},
			error: (error) => {
				console.error('Error loading organizations:', error);
				this.hasError = true;
				this.errorMessage = 'Error loading organizations';
			},
		});
	}

	filterOrganizations(): void {
		let filtered: OrgReturn[];
		
		if (!this.searchText || this.searchText.trim() === '') {
			filtered = [...this.organizations];
		} else {
			const searchTerms = this.searchText.toLowerCase().trim().split(' ');

			filtered = this.organizations.filter((org) => {
				const name = org.name.toLowerCase();

				return searchTerms.every((term) => name.includes(term));
			});
		}
		
		// Apply sorting
		this.filteredOrganizations = this.applySorting(filtered);
	}

	clearSearch(): void {
		this.searchText = '';
		this.filterOrganizations();
	}

	openAddOrgModal(): void {
		this.errors = [];
		// Reset form
		this.newOrganization = {
			name: '',
		};

		this.addOrgModalRef = this.modalService.open(this.addOrgModalTemplate, {
			centered: true,
		});
	}

	openEditOrgModal(org: OrgReturn): void {
		this.errors = [];
		this.editingOrganization = { ...org };

		this.editOrgModalRef = this.modalService.open(
			this.editOrgModalTemplate,
			{
				centered: true,
			},
		);
	}

	addOrganization(): void {
		const orgCreationRequest: OrgRequest = {
			Id: 0, // Set to 0 for new organization
			name: this.newOrganization.name,
		};

		// Enable loading state
		this.isCreatingOrganization = true;

		this.backendService.CreateOrg(orgCreationRequest).subscribe({
			next: (orgId: number) => {
				// Disable loading state
				this.isCreatingOrganization = false;

				// Success - organization created
				const newOrg: OrgReturn = {
					id: orgId,
					name: this.newOrganization.name,
					users: [],
				};

				// Add to local list if it's not already there
				if (!this.organizations.some((org) => org.id === orgId)) {
					this.organizations.push(newOrg);
					this.filterOrganizations();
				}

				this.notificationService.ShowToast(
					`Organisaatio ${this.newOrganization.name} lisätty`,
					'success',
				);

				if (this.addOrgModalRef) {
					this.addOrgModalRef.close();
				}

				// Refresh organizations from server
				this.refreshOrganizations();
			},
			error: (error: ErrorResponse) => {
				// Disable loading state
				this.isCreatingOrganization = false;

				console.error('Error creating organization:', error);
				this.notificationService.ShowToast(
					'Organisaation lisääminen epäonnistui. Tarkista tiedot ja yritä uudelleen.',
					'danger',
				);

				this.errors = [];
				if (error?.errors) {
					error.errors.forEach((e: string) => {
						this.errors.push(e);
					});
				}
			},
		});
	}

	updateOrganization(): void {
		if (!this.editingOrganization) return;

		const orgUpdateRequest: OrgRequest = {
			Id: this.editingOrganization.id,
			name: this.editingOrganization.name,
		};

		// Enable loading state
		this.isUpdatingOrganization = true;

		this.backendService.UpdateOrg(orgUpdateRequest).subscribe({
			next: () => {
				// Disable loading state
				this.isUpdatingOrganization = false;

				// Success - organization updated
				const index = this.organizations.findIndex(
					(org) => org.id === this.editingOrganization?.id,
				);
				if (index !== -1 && this.editingOrganization) {
					this.organizations[index] = { ...this.editingOrganization };
					this.filterOrganizations();
				}

				this.notificationService.ShowToast(
					`Organisaatio ${this.editingOrganization?.name} päivitetty`,
					'success',
				);

				if (this.editOrgModalRef) {
					this.editOrgModalRef.close();
				}

				// Refresh organizations from server
				this.refreshOrganizations();
			},
			error: (error: ErrorResponse) => {
				// Disable loading state
				this.isUpdatingOrganization = false;

				console.error('Error updating organization:', error);
				this.notificationService.ShowToast(
					'Organisaation päivittäminen epäonnistui.',
					'danger',
				);
			},
		});
	}

	CountOrganizationUsers(orgId: number): number {
		const org = this.organizations.find((o) => o.id === orgId);
		return org ? org.users.length : 0;
	}

	deleteOrganization(org: OrgReturn): void {
		const modalRef = this.modalService.open(ConfirmModalComponent);
		modalRef.componentInstance.title = 'Poista organisaatio';
		modalRef.componentInstance.message = `Haluatko varmasti poistaa organisaation ${org.name}?`;
		modalRef.componentInstance.confirmButtonText = 'Poista';
		modalRef.componentInstance.confirmButtonClass = 'btn-danger';
		modalRef.componentInstance.manualClose = true;

		// Subscribe to confirmed event when manualClose is enabled
		modalRef.componentInstance.confirmed.subscribe(() => {
			// Enable loading state
			modalRef.componentInstance.loading = true;

			this.backendService.DeleteOrg(org.id).subscribe({
				next: () => {
					// Close modal on success
					modalRef.close();

					// Remove organization from local list
					this.organizations = this.organizations.filter(
						(o) => o.id !== org.id,
					);
					this.filterOrganizations(); // Refresh filtered list

					this.notificationService.ShowToast(
						`Organisaatio ${org.name} poistettu`,
						'success',
					);

					// Refresh organizations from server
					this.refreshOrganizations();
				},
				error: (error: ErrorResponse) => {
					// Close modal on error
					modalRef.close();

					console.error('Error deleting organization:', error);
					this.notificationService.ShowToast(
						'Organisaation poistaminen epäonnistui.',
						'danger',
					);
				},
			});
		});
	}

	/**
	 * Sorts the organizations by the specified column
	 */
	sortOrganizations(column: 'name' | 'userCount'): void {
		if (this.sortColumn === column) {
			// Toggle direction if clicking same column
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			// Set new column and start with descending if we were already ascending,
			// otherwise start with ascending
			this.sortColumn = column;
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		}
		
		// Re-apply filtering which includes sorting
		this.filterOrganizations();
	}
	
	/**
	 * Applies sorting to the given organizations array
	 */
	private applySorting(organizations: OrgReturn[]): OrgReturn[] {
		if (!this.sortColumn) {
			return organizations;
		}

		return [...organizations].sort((a, b) => {
			let aValue: string | number;
			let bValue: string | number;

			switch (this.sortColumn) {
				case 'name':
					aValue = a.name.toLowerCase();
					bValue = b.name.toLowerCase();
					break;
				case 'userCount':
					aValue = a.users.length;
					bValue = b.users.length;
					break;
				default:
					return 0;
			}

			let comparison = 0;
			if (aValue < bValue) {
				comparison = -1;
			} else if (aValue > bValue) {
				comparison = 1;
			}

			return this.sortDirection === 'asc' ? comparison : -comparison;
		});
	}
	
	/**
	 * Gets the sort icon class for a column
	 */
	getSortIcon(column: 'name' | 'userCount'): string {
		if (this.sortColumn !== column) {
			return 'bi-arrow-down-up'; // Default sort icon
		}
		return this.sortDirection === 'asc' ? 'bi-sort-alpha-down' : 'bi-sort-alpha-up';
	}

	private refreshOrganizations(): void {
		// Trigger refresh through login service
		// This will automatically update our subscription
		this.loginService.RefreshOrganizations().subscribe({
			next: (organizations) => {
				console.log('Organizations refreshed:', organizations);
			},
			error: (error) => {
				console.error('Error refreshing organizations:', error);
			},
		});
	}

	// Helper method to check if we have modals open
	HasOtherModalsOpen(): boolean {
		return !!this.addOrgModalRef || !!this.editOrgModalRef;
	}

	// Close any open modal
	closeModal(): void {
		if (this.addOrgModalRef) {
			this.addOrgModalRef.close();
			this.addOrgModalRef = undefined;
		}

		if (this.editOrgModalRef) {
			this.editOrgModalRef.close();
			this.editOrgModalRef = undefined;
		}
	}
}
